/**
 * ExploreIndiaLanding — /explore/india
 *
 * Auto-generated public front door for everything the Geographic
 * Discovery Engine has produced. Three main sections:
 *
 *   1. Totals hero (locations / POIs / topics / regions)
 *   2. States completed this week (chips)
 *   3. Top 20 cities by POI count (map-style grid)
 *   4. Trending topics with hero images (card grid)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Globe2, Sparkles, MapPin, TrendingUp, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function prettifyKey(k) {
  return String(k || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ExploreIndiaLanding() {
  const [d, setD] = useState(null);
  const [loading, setLoad] = useState(true);
  useEffect(() => {
    axios.get(`${API}/api/geo/explore/india`)
      .then((r) => setD(r.data))
      .catch(() => setD(null))
      .finally(() => setLoad(false));
  }, []);

  return (
    <main className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="explore-india-landing">
      <nav className="text-xs opacity-70 mb-4">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-2">/</span>
        <span>Explore India</span>
      </nav>

      <div className="flex items-center gap-2 mb-2">
        <Globe2 className="w-5 h-5" />
        <h1 className="font-display text-3xl sm:text-4xl">Explore India — live</h1>
      </div>
      <p className="text-sm opacity-70 mb-8 max-w-2xl">
        Every location, POI, and topic on Social Pulse is generated and
        indexed automatically. This page is a live front door — refresh
        it any time to see what's new.
      </p>

      {loading && <div className="text-sm opacity-70 py-10"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading discovery feed…</div>}

      {d && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="explore-totals">
            {[
              { label: "Locations", value: d.totals.locations, color: "#1D4ED8" },
              { label: "POIs",      value: d.totals.pois,      color: "#B91C1C" },
              { label: "Topics",    value: d.totals.topics,    color: "#B45309" },
              { label: "Regions",   value: d.totals.regions,   color: "#065F46" },
            ].map((k) => (
              <div key={k.label} className="rounded-2xl border p-4"
                style={{ borderColor: `${k.color}33`, background: "var(--wofy-surface)" }}
                data-testid={`explore-total-${k.label.toLowerCase()}`}
              >
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: k.color }}>{k.label}</div>
                <div className="text-2xl sm:text-3xl font-display mt-1 tabular-nums">{k.value.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {/* Recent sweeps */}
          {d.recent_sweeps?.length > 0 && (
            <section className="mb-10" data-testid="explore-recent-sweeps">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" />
                <h2 className="font-display text-xl">States completed this week</h2>
                <span className="text-xs opacity-60">{d.recent_sweeps.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {d.recent_sweeps.map((s) => {
                  // "sri-lanka" / "bhutan" are country slugs — link to /l/<slug>;
                  // Indian states → /l/india/<slug>.
                  const looksLikeCountry = ["sri-lanka","nepal","bangladesh","bhutan"].includes(s.key);
                  const href = looksLikeCountry ? `/l/${s.key}` : `/l/india/${s.key}`;
                  return (
                    <Link
                      key={s.key}
                      to={href}
                      className="inline-flex items-center gap-1 text-sm rounded-full border px-3 py-1.5 hover:bg-black/[0.04] transition"
                      style={{ borderColor: "var(--wofy-border)" }}
                      data-testid={`explore-recent-${s.key}`}
                    >
                      <span className="font-medium">{prettifyKey(s.key)}</span>
                      <span className="text-[10px] opacity-60">{s.total} places</span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Top cities */}
          {d.top_cities?.length > 0 && (
            <section className="mb-10" data-testid="explore-top-cities">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4" />
                <h2 className="font-display text-xl">Top cities by POI count</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.top_cities.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/l/${c.slug}`}
                    className="rounded-xl border p-3 hover:shadow-md transition"
                    style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                    data-testid={`explore-city-${c.slug}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs opacity-70 mt-0.5">
                          {[c.state, c.country].filter(Boolean).join(" · ") || c.slug}
                        </div>
                      </div>
                      <span className="text-xs font-mono px-2 py-0.5 rounded-full border tabular-nums"
                        style={{ borderColor: "var(--wofy-border)" }}
                      >{c.pois}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Trending topics */}
          {d.trending_topics?.length > 0 && (
            <section className="mb-10" data-testid="explore-trending-topics">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4" />
                <h2 className="font-display text-xl">Trending topics</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.trending_topics.map((t) => (
                  <Link
                    key={t.slug}
                    to={t.deep_link || `/topic/${t.slug.split("-").slice(1).join("-")}`}
                    className="rounded-2xl border overflow-hidden group"
                    style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                    data-testid={`explore-trending-${t.slug}`}
                  >
                    <div className="aspect-video bg-black/5 overflow-hidden">
                      {t.hero_image?.url && (
                        <img src={t.hero_image.url} alt="" loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition" />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{t.source || "topic"}</div>
                      <div className="font-medium mt-0.5 group-hover:underline">{t.title}</div>
                      {(t.tags || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {t.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-[10px] rounded-full border px-1.5 py-0.5 opacity-70"
                              style={{ borderColor: "var(--wofy-border)" }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
